import type { PageDefinition } from '@/types/page'
import {
  ALERT_STATUS,
  CHANNEL_OPTIONS,
  DEVICE_STATUS,
  FREQUENCY_OPTIONS,
  METRIC_OPTIONS,
  PLAN_STATUS,
  RECORD_STATUS,
  REPORT_STATUS,
  RULE_STATUS,
  SEVERITY,
  TIMING_OPTIONS,
  toOptions,
} from './common'

export const medicationPlansPage: PageDefinition = {
  id: 'medication-plans',
  route: '#/medication/plans',
  title: '用药计划',
  purpose: '维护用户用药方案、提醒时间与负责人，观察执行依从与逾期风险',
  group: '用药管理',
  icon: 'pill',
  kind: 'table',
  permission: 'medication.view',
  permissions: {
    create: 'medication.create',
    edit: 'medication.edit',
    pause: 'medication.pause',
    remind: 'medication.remind',
    import: 'medication.import',
    export: 'medication.export',
  },
  searchKeys: ['plan_no', 'user_name', 'family_name', 'drug_name', 'owner_name'],
  statusKey: 'status',
  statusMap: PLAN_STATUS,
  timeField: 'start_date',
  summary: [
    { key: 'total', label: '计划总数', kind: 'count', hint: '覆盖全部家庭成员用药方案' },
    { key: 'active', label: '执行中', kind: 'count', where: { status: 'active' }, hint: '按提醒时间持续执行' },
    { key: 'paused', label: '已暂停', kind: 'count', where: { status: 'paused' }, hint: '暂停均保留原因与历史' },
    { key: 'overdue', label: '已逾期', kind: 'count', where: { status: 'overdue' }, hint: '需优先联系确认' },
    { key: 'adherence', label: '平均依从率', kind: 'ratio', field: 'adherence', suffix: '%', hint: '按当前计划近 7 日计算' },
  ],
  columns: [
    { key: 'plan_no', label: '计划编号', width: 118, sortable: true },
    { key: 'user_name', label: '用户', type: 'primary', subtitleKey: 'family_name', minWidth: 104 },
    { key: 'drug_name', label: '药品', minWidth: 142 },
    { key: 'dosage', label: '剂量', width: 84 },
    { key: 'frequency', label: '频次', width: 78 },
    { key: 'timing', label: '服用时机', type: 'tag', minWidth: 112 },
    { key: 'start_date', label: '开始日期', type: 'date', width: 104, sortable: true },
    { key: 'end_date', label: '结束日期', type: 'date', width: 104, hideByDefault: true },
    { key: 'owner_name', label: '负责人', width: 82 },
    { key: 'adherence', label: '依从率', type: 'percent', width: 82, align: 'right' },
    { key: 'status', label: '执行状态', type: 'status', width: 86 },
    { key: 'last_reminded_at', label: '最近提醒', type: 'datetime', width: 138 },
    { key: 'next_remind_at', label: '下次提醒', type: 'datetime', width: 138, hideByDefault: true },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '计划编号、用户、家庭或药品', width: 238 },
    { key: 'status', label: '执行状态', type: 'select', options: toOptions(PLAN_STATUS), width: 116 },
    { key: 'family_id', label: '所属家庭', type: 'select', optionSource: 'families', width: 146 },
    { key: 'owner_id', label: '负责人', type: 'select', optionSource: 'care_team', width: 130 },
    { key: 'risk_level', label: '健康风险', type: 'select', options: [{ value: '高风险', label: '高风险' }, { value: '中风险', label: '中风险' }, { value: '低风险', label: '低风险' }], width: 112 },
    { key: 'start_date', label: '开始日期', type: 'daterange', timeField: 'start_date', width: 230 },
  ],
  formFields: [
    { key: 'plan_no', label: '计划编号', type: 'readonly', readonlyOnEdit: true, placeholder: '保存后自动生成' },
    { key: 'user_id', label: '用户', type: 'select', optionSource: 'users', required: true },
    { key: 'family_id', label: '所属家庭', type: 'select', optionSource: 'families', required: true },
    { key: 'drug_id', label: '药品', type: 'select', optionSource: 'approved_drugs', required: true, help: '仅可选择已上架且未下架的药品' },
    { key: 'dosage', label: '每次剂量', type: 'text', required: true, placeholder: '如：每次1片' },
    { key: 'frequency', label: '服用频次', type: 'select', options: FREQUENCY_OPTIONS, required: true },
    { key: 'timing', label: '提醒时机', type: 'multiselect', options: TIMING_OPTIONS, required: true },
    { key: 'start_date', label: '开始日期', type: 'date', required: true },
    { key: 'end_date', label: '结束日期', type: 'date', required: true },
    { key: 'owner_id', label: '跟进负责人', type: 'select', optionSource: 'care_team', required: true },
    { key: 'status', label: '计划状态', type: 'select', options: toOptions(PLAN_STATUS), defaultValue: 'active' },
    { key: 'reminder_note', label: '提醒备注', type: 'textarea', full: true, maxLength: 160, placeholder: '将展示在用户端提醒中' },
  ],
  createLabel: '新建用药计划',
  createPermission: 'medication.create',
  editPermission: 'medication.edit',
  importPermission: 'medication.import',
  exportPermission: 'medication.export',
  exportScope: 'current-page',
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'medication.edit', tone: 'primary' },
    { key: 'remind', label: '发送提醒', permission: 'medication.remind' },
    {
      key: 'pause',
      label: '暂停',
      permission: 'medication.pause',
      tone: 'danger',
      confirm: true,
      form: [{ key: 'reason', label: '暂停原因', type: 'textarea', required: true, full: true, placeholder: '暂停后从恢复操作的下一个提醒时间继续' }],
      visibleWhen: { field: 'status', in: ['active', 'overdue'] },
    },
    { key: 'resume', label: '恢复', permission: 'medication.pause', tone: 'primary', visibleWhen: { field: 'status', in: ['paused'] } },
    { key: 'timeline', label: '时间线' },
  ],
  batchActions: [
    { key: 'batch-import', label: '批量导入', permission: 'medication.import', kind: 'task' },
    { key: 'batch-remind', label: '批量提醒', permission: 'medication.remind', kind: 'task' },
    { key: 'batch-export', label: '导出当前页', permission: 'medication.export', kind: 'export' },
  ],
  detailSections: [
    { title: '计划信息', fields: ['plan_no', 'user_name', 'family_name', 'drug_name', 'dosage', 'frequency', 'timing', 'start_date', 'end_date', 'owner_name', 'status'] },
    { title: '执行概况', fields: ['adherence', 'last_reminded_at', 'next_remind_at', 'pause_reason', 'created_at', 'updated_at'] },
  ],
  detailMetrics: [
    { label: '近7日依从率', field: 'adherence', type: 'percent' },
    { label: '累计提醒', field: 'remind_count', type: 'number' },
    { label: '漏服次数', field: 'missed_count', type: 'number' },
  ],
  notice: '暂停计划必须填写原因；恢复后从下一个提醒时间继续，历史记录不会被覆盖。',
  emptyHint: '当前筛选下没有用药计划。',
  numberPrefix: 'YY',
  seedKey: 'medication_plans',
}

export const medicationRecordsPage: PageDefinition = {
  id: 'medication-records',
  route: '#/medication/records',
  title: '用药记录',
  purpose: '查看用户服药确认、逾期与跳过记录，对异常记录进行留痕更正',
  group: '用药管理',
  icon: 'records',
  kind: 'table',
  permission: 'medication.records.view',
  permissions: { correct: 'medication.records.correct', export: 'medication.records.export' },
  hideSelection: true,
  readOnly: false,
  searchKeys: ['user_name', 'family_name', 'plan_no', 'drug_name'],
  statusKey: 'status',
  statusMap: RECORD_STATUS,
  timeField: 'scheduled_at',
  summary: [
    { key: 'total', label: '记录总数', kind: 'count', hint: '按计划时间汇总提醒记录' },
    { key: 'taken', label: '已服药', kind: 'count', where: { status: 'taken' }, hint: '用户已确认或家属代确认' },
    { key: 'overdue', label: '已逾期', kind: 'count', where: { status: 'overdue' }, hint: '超过提醒时间未确认' },
    { key: 'skipped', label: '已跳过', kind: 'count', where: { status: 'skipped' }, hint: '用户主动跳过本次用药' },
    { key: 'rate', label: '确认率', kind: 'ratio', where: { status: 'taken' }, suffix: '%', hint: '已确认记录 / 全部记录' },
  ],
  columns: [
    { key: 'scheduled_at', label: '计划时间', type: 'datetime', width: 148, sortable: true },
    { key: 'user_name', label: '用户', type: 'primary', subtitleKey: 'family_name', minWidth: 104 },
    { key: 'drug_name', label: '药品', minWidth: 150 },
    { key: 'dosage', label: '剂量', width: 92 },
    { key: 'plan_no', label: '计划编号', width: 124 },
    { key: 'status', label: '记录状态', type: 'status', width: 92 },
    { key: 'taken_at', label: '确认时间', type: 'datetime', width: 148 },
    { key: 'source', label: '记录来源', width: 100 },
    { key: 'recorded_by', label: '确认人', width: 96 },
    { key: 'owner_name', label: '跟进人', width: 92 },
    { key: 'note', label: '备注', minWidth: 160 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '用户、家庭、药品或计划编号', width: 230 },
    { key: 'status', label: '记录状态', type: 'select', options: toOptions(RECORD_STATUS), width: 116 },
    { key: 'drug_name', label: '药品', type: 'select', optionSource: 'drugs', width: 160 },
    { key: 'family_id', label: '所属家庭', type: 'select', optionSource: 'families', width: 146 },
    { key: 'owner_id', label: '负责人', type: 'select', optionSource: 'care_team', width: 130 },
    { key: 'scheduled_at', label: '计划时间', type: 'daterange', timeField: 'scheduled_at', width: 230 },
  ],
  formFields: [
    { key: 'status', label: '更正状态', type: 'select', options: toOptions(RECORD_STATUS), required: true },
    { key: 'taken_at', label: '实际服药时间', type: 'datetime', required: true },
    { key: 'note', label: '更正原因', type: 'textarea', required: true, full: true, maxLength: 200, help: '原记录会保留，更正原因写入操作日志' },
  ],
  exportPermission: 'medication.records.export',
  exportScope: 'current-page',
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'correct', label: '更正记录', permission: 'medication.records.correct', tone: 'primary', confirm: true, form: [
      { key: 'status', label: '更正状态', type: 'select', options: toOptions(RECORD_STATUS), required: true },
      { key: 'taken_at', label: '实际服药时间', type: 'datetime', required: true },
      { key: 'note', label: '更正原因', type: 'textarea', required: true, full: true, maxLength: 200 },
    ] },
  ],
  detailSections: [
    { title: '记录信息', fields: ['scheduled_at', 'taken_at', 'user_name', 'family_name', 'drug_name', 'dosage', 'plan_no', 'status', 'source', 'recorded_by', 'owner_name'] },
    { title: '留痕信息', fields: ['note', 'corrected_from', 'created_at', 'updated_at'] },
  ],
  notice: '记录以服务端时间为准；更正不会静默覆盖原记录，必须填写原因。',
  emptyHint: '所选日期范围内没有用药记录。',
  seedKey: 'medication_records',
}

export const healthMonitoringPage: PageDefinition = {
  id: 'health-monitoring',
  route: '#/health/monitoring',
  title: '健康监测',
  purpose: '汇总六类健康指标、设备在线情况与异常预警，定位需要重点跟进的用户',
  group: '健康监测',
  icon: 'heart',
  kind: 'dashboard-table',
  permission: 'health.view',
  permissions: {},
  readOnly: true,
  hideSelection: true,
  searchKeys: ['user_name', 'family_name', 'metric_type', 'device_name'],
  statusKey: 'severity',
  statusMap: SEVERITY,
  timeField: 'measured_at',
  summary: [
    { key: 'total', label: '今日上报', kind: 'count', hint: '设备与人工记录合计' },
    { key: 'abnormal', label: '异常指标', kind: 'count', where: { is_abnormal: true }, hint: '已进入预警判断队列' },
    { key: 'high', label: '高风险', kind: 'count', where: { severity: '高' }, hint: '按当前预警规则分级' },
    { key: 'bp', label: '血压记录', kind: 'count', where: { metric_type: '血压' }, hint: '今日血压测量上报' },
    { key: 'rate', label: '异常占比', kind: 'ratio', where: { is_abnormal: true }, suffix: '%', hint: '异常指标 / 全部上报' },
  ],
  columns: [
    { key: 'measured_at', label: '测量时间', type: 'datetime', width: 138, sortable: true },
    { key: 'user_name', label: '用户', type: 'primary', subtitleKey: 'family_name', minWidth: 104 },
    { key: 'metric_type', label: '指标', width: 78 },
    { key: 'value_display', label: '测量值', minWidth: 112 },
    { key: 'reference_range', label: '参考范围', minWidth: 126 },
    { key: 'is_abnormal', label: '是否异常', type: 'boolean', width: 78, align: 'center' },
    { key: 'severity', label: '风险等级', type: 'status', width: 84 },
    { key: 'source', label: '数据来源', width: 94 },
    { key: 'device_name', label: '设备', minWidth: 148 },
    { key: 'region_name', label: '所属区域', minWidth: 88, hideByDefault: true },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '用户、家庭或设备', width: 220 },
    { key: 'metric_type', label: '指标类型', type: 'select', options: METRIC_OPTIONS, width: 132 },
    { key: 'severity', label: '风险等级', type: 'select', options: toOptions(SEVERITY), width: 112 },
    { key: 'region_id', label: '所属区域', type: 'region', optionSource: 'regions', width: 150 },
    { key: 'measured_at', label: '测量时间', type: 'daterange', timeField: 'measured_at', width: 230 },
  ],
  exportScope: 'current-page',
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'create-alert', label: '生成预警', tone: 'primary' },
    { key: 'open-device', label: '查看设备' },
  ],
  detailSections: [
    { title: '测量信息', fields: ['user_name', 'family_name', 'metric_type', 'value_display', 'reference_range', 'is_abnormal', 'severity'] },
    { title: '数据来源', fields: ['measured_at', 'source', 'device_name', 'region_name', 'created_at'] },
  ],
  notice: '健康指标按设备上报与用户手动记录汇总，异常值会进入预警队列。',
  emptyHint: '所选时间范围内没有健康指标上报。',
  seedKey: 'health_metrics',
}

export const healthAlertsPage: PageDefinition = {
  id: 'health-alerts',
  route: '#/health/alerts',
  title: '健康预警',
  purpose: '处理健康指标异常与 SLA 逾期预警，完成指派、跟进、联系家属与闭环',
  group: '健康监测',
  icon: 'alert',
  kind: 'table',
  permission: 'alerts.view',
  permissions: {
    assign: 'alerts.assign',
    handle: 'alerts.handle',
    close: 'alerts.close',
    export: 'alerts.export',
  },
  searchKeys: ['alert_no', 'user_name', 'family_name', 'alert_type', 'rule_name', 'owner_name'],
  statusKey: 'status',
  statusMap: ALERT_STATUS,
  timeField: 'triggered_at',
  summary: [
    { key: 'total', label: '预警总数', kind: 'count' },
    { key: 'pending', label: '待处理', kind: 'count', where: { status: 'pending' } },
    { key: 'high', label: '高风险', kind: 'count', where: { severity: '高' } },
    { key: 'processing', label: '处理中', kind: 'count', where: { status: 'processing' } },
    { key: 'closed', label: '已闭环', kind: 'count', where: { status: 'closed' } },
  ],
  columns: [
    { key: 'alert_no', label: '预警编号', width: 130, sortable: true },
    { key: 'user_name', label: '用户', type: 'primary', subtitleKey: 'family_name', minWidth: 104 },
    { key: 'alert_type', label: '预警类型', width: 92 },
    { key: 'metric_value', label: '异常值', width: 122 },
    { key: 'reference_range', label: '参考范围', minWidth: 140 },
    { key: 'severity', label: '等级', type: 'status', width: 74 },
    { key: 'status', label: '处理状态', type: 'status', width: 92 },
    { key: 'owner_name', label: '跟进人', width: 92 },
    { key: 'triggered_at', label: '触发时间', type: 'datetime', width: 148, sortable: true },
    { key: 'sla_due_at', label: 'SLA 截止', type: 'datetime', width: 148 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '预警编号、用户、家庭或规则', width: 230 },
    { key: 'severity', label: '预警等级', type: 'select', options: toOptions(SEVERITY), width: 112 },
    { key: 'alert_type', label: '预警类型', type: 'select', options: METRIC_OPTIONS, width: 132 },
    { key: 'status', label: '处理状态', type: 'select', options: toOptions(ALERT_STATUS), width: 116 },
    { key: 'region_id', label: '所属区域', type: 'region', optionSource: 'regions', width: 150 },
    { key: 'triggered_at', label: '触发时间', type: 'daterange', timeField: 'triggered_at', width: 230 },
  ],
  exportPermission: 'alerts.export',
  exportScope: 'current-page',
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'assign', label: '指派跟进', permission: 'alerts.assign', tone: 'primary', form: [{ key: 'owner_id', label: '跟进人', type: 'select', optionSource: 'care_team', required: true }] },
    { key: 'contact-family', label: '联系家属', permission: 'alerts.handle', confirm: true, form: [{ key: 'note', label: '沟通记录', type: 'textarea', required: true, full: true }] },
    { key: 'generate-report', label: '生成报告', permission: 'reports.generate' },
    {
      key: 'handle',
      label: '标记已处理',
      permission: 'alerts.handle',
      form: [{ key: 'note', label: '处理说明', type: 'textarea', required: true, full: true }],
      visibleWhen: { field: 'status', in: ['pending', 'processing'] },
    },
    {
      key: 'close',
      label: '关闭预警',
      permission: 'alerts.close',
      tone: 'danger',
      confirm: true,
      confirmText: '关闭后预警进入闭环，不再参与 SLA 计时，请确认已联系用户或家属。',
      form: [{ key: 'close_reason', label: '关闭原因', type: 'textarea', required: true, full: true }],
      visibleWhen: { field: 'status', in: ['pending', 'processing', 'resolved'] },
    },
  ],
  batchActions: [
    { key: 'batch-assign', label: '批量指派', permission: 'alerts.assign', kind: 'task', form: [{ key: 'owner_id', label: '跟进人', type: 'select', optionSource: 'care_team', required: true }] },
    { key: 'batch-close', label: '批量关闭', permission: 'alerts.close', tone: 'danger', confirm: true, kind: 'transition', form: [{ key: 'reason', label: '关闭原因', type: 'textarea', required: true, full: true }] },
    { key: 'batch-export', label: '导出当前页', permission: 'alerts.export', kind: 'export' },
  ],
  detailSections: [
    { title: '预警信息', fields: ['alert_no', 'user_name', 'family_name', 'rule_name', 'alert_type', 'metric_value', 'reference_range', 'severity', 'status'] },
    { title: '处理进度', fields: ['owner_name', 'triggered_at', 'sla_due_at', 'closed_at', 'close_reason', 'updated_at'] },
  ],
  detailMetrics: [
    { label: 'SLA 剩余', field: 'sla_remaining', type: 'text' },
    { label: '关联指标', field: 'metric_count', type: 'number' },
    { label: '历史预警', field: 'history_alerts', type: 'number' },
  ],
  notice: '高风险预警需在 SLA 内完成首响；关闭预警必须填写原因并保留处理记录。',
  emptyHint: '当前筛选下没有健康预警。',
  seedKey: 'alerts',
}

export const alertRulesPage: PageDefinition = {
  id: 'alert-rules',
  route: '#/health/alert-rules',
  title: '预警规则',
  purpose: '配置指标阈值、持续时长、适用人群、等级与通知渠道，并保留规则版本',
  group: '健康监测',
  icon: 'rules',
  kind: 'table',
  permission: 'alerts.rules.view',
  permissions: { create: 'alerts.rules.edit', edit: 'alerts.rules.edit', toggle: 'alerts.rules.edit' },
  searchKeys: ['name', 'metric_type', 'audience'],
  statusKey: 'status',
  statusMap: RULE_STATUS,
  summary: [
    { key: 'total', label: '规则总数', kind: 'count' },
    { key: 'active', label: '启用规则', kind: 'count', where: { status: 'active' } },
    { key: 'high', label: '高风险规则', kind: 'count', where: { severity: '高' } },
    { key: 'versions', label: '规则版本', kind: 'sum', field: 'version', suffix: ' 版' },
  ],
  columns: [
    { key: 'name', label: '规则名称', type: 'primary', subtitleKey: 'metric_type', minWidth: 160, sortable: true },
    { key: 'audience', label: '适用人群', width: 112 },
    { key: 'lower_bound', label: '下限', type: 'number', width: 80, align: 'right' },
    { key: 'upper_bound', label: '上限', type: 'number', width: 80, align: 'right' },
    { key: 'duration_minutes', label: '持续时长', type: 'number', width: 92, align: 'right', suffix: ' 分钟' },
    { key: 'severity', label: '预警等级', type: 'status', width: 90 },
    { key: 'channels', label: '通知渠道', type: 'tag', minWidth: 140 },
    { key: 'version', label: '版本', type: 'number', width: 68, align: 'center' },
    { key: 'status', label: '状态', type: 'status', width: 80 },
    { key: 'updated_at', label: '更新时间', type: 'datetime', width: 148 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '规则名称或指标', width: 220 },
    { key: 'severity', label: '预警等级', type: 'select', options: toOptions(SEVERITY), width: 112 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(RULE_STATUS), width: 104 },
    { key: 'metric_type', label: '指标', type: 'select', options: METRIC_OPTIONS, width: 132 },
  ],
  formFields: [
    { key: 'name', label: '规则名称', type: 'text', required: true, maxLength: 40 },
    { key: 'metric_type', label: '监测指标', type: 'select', options: METRIC_OPTIONS, required: true },
    { key: 'lower_bound', label: '下限', type: 'number', required: true },
    { key: 'upper_bound', label: '上限', type: 'number', required: true },
    { key: 'duration_minutes', label: '持续时长', type: 'number', min: 0, max: 1440, required: true, suffix: '分钟' },
    { key: 'audience', label: '适用人群', type: 'select', options: ['全部用户', '高血压人群', '60岁以上', '高风险家庭'].map(value => ({ value, label: value })), required: true },
    { key: 'severity', label: '预警等级', type: 'select', options: toOptions(SEVERITY), required: true },
    { key: 'channels', label: '通知渠道', type: 'multiselect', options: CHANNEL_OPTIONS, required: true },
    { key: 'status', label: '状态', type: 'select', options: toOptions(RULE_STATUS), defaultValue: 'active' },
  ],
  createLabel: '新增规则',
  createPermission: 'alerts.rules.edit',
  editPermission: 'alerts.rules.edit',
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'alerts.rules.edit', tone: 'primary' },
    { key: 'test-rule', label: '样例测试' },
    { key: 'version-history', label: '版本历史' },
    { key: 'toggle-rule', label: '停用', permission: 'alerts.rules.edit', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['active'] } },
    { key: 'toggle-rule', label: '启用', permission: 'alerts.rules.edit', visibleWhen: { field: 'status', in: ['disabled'] } },
  ],
  detailSections: [
    { title: '规则配置', fields: ['name', 'metric_type', 'lower_bound', 'upper_bound', 'duration_minutes', 'audience', 'severity', 'channels'] },
    { title: '版本信息', fields: ['version', 'status', 'created_at', 'updated_at'] },
  ],
  notice: '规则变更后新版本立即生效，历史预警保留触发时的规则快照。',
  emptyHint: '还没有配置预警规则。',
  seedKey: 'alert_rules',
}

export const healthReportsPage: PageDefinition = {
  id: 'health-reports',
  route: '#/health/reports',
  title: '健康报告',
  purpose: '生成、发送与重试家庭健康报告，维护报告模板并追踪失败原因',
  group: '健康监测',
  icon: 'report',
  kind: 'table',
  permission: 'reports.view',
  permissions: { generate: 'reports.generate', send: 'reports.send', template: 'reports.template' },
  searchKeys: ['report_no', 'user_name', 'family_name', 'template_name'],
  statusKey: 'status',
  statusMap: REPORT_STATUS,
  timeField: 'created_at',
  summary: [
    { key: 'total', label: '报告总数', kind: 'count' },
    { key: 'completed', label: '已完成', kind: 'count', where: { status: 'completed' } },
    { key: 'sent', label: '已发送', kind: 'count', where: { status: 'sent' } },
    { key: 'failed', label: '生成失败', kind: 'count', where: { status: 'failed' } },
  ],
  columns: [
    { key: 'report_no', label: '报告编号', width: 130, sortable: true },
    { key: 'user_name', label: '用户', type: 'primary', subtitleKey: 'family_name', minWidth: 104 },
    { key: 'report_type', label: '报告类型', width: 96 },
    { key: 'period_start', label: '统计周期', type: 'date', width: 116 },
    { key: 'period_end', label: '至', type: 'date', width: 116 },
    { key: 'template_name', label: '报告模板', minWidth: 180 },
    { key: 'status', label: '状态', type: 'status', width: 92 },
    { key: 'generated_at', label: '生成时间', type: 'datetime', width: 148 },
    { key: 'sent_at', label: '发送时间', type: 'datetime', width: 148 },
    { key: 'failure_reason', label: '失败原因', minWidth: 180 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '报告编号、用户或模板', width: 220 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(REPORT_STATUS), width: 110 },
    { key: 'report_type', label: '报告类型', type: 'select', options: ['周报', '月报', '季度报告'].map(value => ({ value, label: value })), width: 118 },
    { key: 'user_id', label: '用户', type: 'select', optionSource: 'users', width: 150 },
    { key: 'created_at', label: '创建时间', type: 'daterange', timeField: 'created_at', width: 230 },
  ],
  createLabel: '生成健康报告',
  createPermission: 'reports.generate',
  formFields: [
    { key: 'report_no', label: '报告编号', type: 'readonly', readonlyOnEdit: true, help: '保存后自动生成，可在详情中查看', placeholder: '保存后自动生成' },
    { key: 'user_id', label: '报告用户', type: 'select', optionSource: 'users', required: true, help: '报告将关联该用户的家庭与健康数据' },
    { key: 'report_type', label: '报告类型', type: 'select', options: ['周报', '月报', '季度报告'].map(value => ({ value, label: value })), required: true },
    { key: 'template_name', label: '报告模板', type: 'select', optionSource: 'templates', required: true, help: '仅可选择启用中的模板' },
    { key: 'period_start', label: '统计开始', type: 'date', required: true },
    { key: 'period_end', label: '统计结束', type: 'date', required: true },
  ],
  extraActions: [
    { key: 'templates', label: '报告模板', icon: 'template', kind: 'link', to: '#/health/reports?panel=templates', permission: 'reports.template' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'download', label: '下载', visibleWhen: { field: 'status', in: ['completed', 'sent'] } },
    { key: 'send', label: '发送', permission: 'reports.send', tone: 'primary', confirm: true, visibleWhen: { field: 'status', in: ['completed'] } },
    { key: 'retry', label: '重试生成', permission: 'reports.generate', visibleWhen: { field: 'status', in: ['failed'] } },
    { key: 'generate', label: '重新生成', permission: 'reports.generate', confirm: true },
  ],
  batchActions: [
    { key: 'batch-generate', label: '批量生成', permission: 'reports.generate', kind: 'task', form: [
      { key: 'report_type', label: '报告类型', type: 'select', options: ['周报', '月报', '季度报告'].map(value => ({ value, label: value })), required: true },
      { key: 'template_name', label: '报告模板', type: 'select', optionSource: 'templates', required: true },
    ] },
    { key: 'template-manage', label: '报告模板', permission: 'reports.template', kind: 'task' },
  ],
  detailSections: [
    { title: '报告信息', fields: ['report_no', 'user_name', 'family_name', 'report_type', 'period_start', 'period_end', 'template_name', 'status'] },
    { title: '生成与发送', fields: ['generated_at', 'sent_at', 'failure_reason', 'asset_name'] },
  ],
  notice: '报告生成使用统一模板与指标口径；失败后可重试，发送后用户端可见。',
  emptyHint: '当前筛选下没有健康报告。',
  numberPrefix: 'BG',
  seedKey: 'reports',
}

export const careTeamPage: PageDefinition = {
  id: 'care-team',
  route: '#/health/care-team',
  title: '医护团队',
  purpose: '维护健康顾问、医师与康复师资料，分配负责家庭并观察服务工作量',
  group: '用药管理',
  icon: 'team',
  kind: 'table',
  permission: 'careteam.view',
  permissions: { create: 'careteam.manage', edit: 'careteam.manage', assign: 'careteam.assign', toggle: 'careteam.manage' },
  searchKeys: ['name', 'title', 'specialty', 'region_name', 'phone'],
  statusKey: 'status',
  statusMap: { active: { value: 'active', label: '启用', tone: 'success' }, disabled: { value: 'disabled', label: '停用', tone: 'neutral' } },
  summary: [
    { key: 'total', label: '团队成员', kind: 'count' },
    { key: 'active', label: '在岗', kind: 'count', where: { status: 'active' } },
    { key: 'families', label: '负责家庭', kind: 'sum', field: 'family_count', suffix: ' 户' },
    { key: 'workload', label: '平均工作量', kind: 'ratio', field: 'workload', suffix: '%' },
  ],
  columns: [
    { key: 'name', label: '姓名', type: 'primary', subtitleKey: 'title', minWidth: 110, sortable: true },
    { key: 'specialty', label: '专长', minWidth: 120 },
    { key: 'region_name', label: '服务区域', width: 110 },
    { key: 'phone', label: '联系电话', type: 'phone', width: 130 },
    { key: 'family_count', label: '负责家庭', type: 'number', width: 96, align: 'right', suffix: ' 户' },
    { key: 'workload', label: '工作量', type: 'progress', width: 100 },
    { key: 'status', label: '状态', type: 'status', width: 80 },
    { key: 'joined_at', label: '加入时间', type: 'datetime', width: 148 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '姓名、专长或电话', width: 220 },
    { key: 'specialty', label: '专长', type: 'select', options: ['心血管内科', '内分泌科', '慢病管理', '呼吸内科', '临床营养', '运动康复', '老年医学', '临床药学', '神经内科'].map(value => ({ value, label: value })), width: 140 },
    { key: 'region_id', label: '服务区域', type: 'region', optionSource: 'regions', width: 150 },
    { key: 'status', label: '状态', type: 'select', options: [{ value: 'active', label: '启用' }, { value: 'disabled', label: '停用' }], width: 104 },
  ],
  formFields: [
    { key: 'name', label: '姓名', type: 'text', required: true, maxLength: 20 },
    { key: 'title', label: '职称', type: 'select', options: ['主任医师', '副主任医师', '健康管理师', '营养师', '康复师', '药师'].map(value => ({ value, label: value })), required: true },
    { key: 'specialty', label: '专长', type: 'text', required: true, maxLength: 30 },
    { key: 'region_id', label: '服务区域', type: 'region', optionSource: 'regions', required: true },
    { key: 'phone', label: '联系电话', type: 'phone', required: true, pattern: '^1[3-9]\\d{9}$', patternMessage: '请输入11位有效手机号' },
    { key: 'bio', label: '个人简介', type: 'textarea', full: true, maxLength: 300 },
    { key: 'status', label: '状态', type: 'select', options: [{ value: 'active', label: '启用' }, { value: 'disabled', label: '停用' }], defaultValue: 'active' },
  ],
  createLabel: '新增团队成员',
  createPermission: 'careteam.manage',
  editPermission: 'careteam.manage',
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'careteam.manage', tone: 'primary' },
    { key: 'assign-families', label: '分配家庭', permission: 'careteam.assign', form: [{ key: 'family_ids', label: '负责家庭', type: 'multiselect', optionSource: 'families', required: true }] },
    { key: 'transfer-team', label: '转交服务', permission: 'careteam.assign', form: [
      { key: 'target_id', label: '接收成员', type: 'select', optionSource: 'care_team', required: true, help: '仅启用中的成员可接收家庭与未关闭预警' },
      { key: 'note', label: '转交说明', type: 'textarea', required: true, full: true, maxLength: 160 },
    ] },
    { key: 'workload', label: '工作量' },
    { key: 'toggle-team', label: '停用', permission: 'careteam.manage', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['active'] } },
    { key: 'toggle-team', label: '启用', permission: 'careteam.manage', visibleWhen: { field: 'status', in: ['disabled'] } },
  ],
  detailSections: [
    { title: '成员资料', fields: ['name', 'title', 'specialty', 'region_name', 'phone', 'bio'] },
    { title: '服务情况', fields: ['family_count', 'workload', 'status', 'joined_at'] },
  ],
  notice: '停用成员前需转交其负责家庭与待处理预警。',
  emptyHint: '没有匹配的团队成员。',
  seedKey: 'care_team',
}

export const devicesPage: PageDefinition = {
  id: 'devices',
  route: '#/devices',
  title: '设备管理',
  purpose: '维护设备型号、绑定关系、在线状态与固件任务，处理设备故障',
  group: '健康监测',
  icon: 'device',
  kind: 'table',
  permission: 'devices.view',
  permissions: { manage: 'devices.manage', bind: 'devices.bind', command: 'devices.command', export: 'devices.export' },
  searchKeys: ['device_no', 'model_name', 'user_name', 'family_name', 'region_name'],
  statusKey: 'status',
  statusMap: DEVICE_STATUS,
  timeField: 'last_report_at',
  summary: [
    { key: 'total', label: '设备总数', kind: 'count' },
    { key: 'online', label: '在线设备', kind: 'count', where: { status: 'online' } },
    { key: 'fault', label: '故障设备', kind: 'count', where: { status: 'fault' } },
    { key: 'unbound', label: '未绑定', kind: 'count', where: { status: 'unbound' } },
    { key: 'battery', label: '低电量', kind: 'count', where: { battery_lte: 20 } },
  ],
  columns: [
    { key: 'device_no', label: '设备编号', type: 'primary', subtitleKey: 'model_name', width: 160, sortable: true },
    { key: 'device_type', label: '设备类型', width: 100 },
    { key: 'user_name', label: '绑定用户', minWidth: 110 },
    { key: 'family_name', label: '家庭', width: 96 },
    { key: 'region_name', label: '所属区域', width: 110 },
    { key: 'status', label: '在线状态', type: 'status', width: 92 },
    { key: 'battery', label: '电量', type: 'progress', width: 96 },
    { key: 'firmware', label: '固件版本', width: 92 },
    { key: 'last_report_at', label: '最近上报', type: 'datetime', width: 148 },
    { key: 'bound_at', label: '绑定时间', type: 'datetime', width: 148 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '设备编号、型号或用户', width: 230 },
    { key: 'device_type', label: '设备类型', type: 'select', options: ['血压计', '血糖仪', '智能手环', '血氧仪', '体脂秤', '心电记录仪'].map(value => ({ value, label: value })), width: 120 },
    { key: 'status', label: '在线状态', type: 'select', options: toOptions(DEVICE_STATUS), width: 112 },
    { key: 'region_id', label: '所属区域', type: 'region', optionSource: 'regions', width: 150 },
    { key: 'bound', label: '绑定情况', type: 'select', options: [{ value: 'true', label: '已绑定' }, { value: 'false', label: '未绑定' }], width: 110 },
    { key: 'last_report_at', label: '最近上报', type: 'daterange', timeField: 'last_report_at', width: 230 },
  ],
  formFields: [
    { key: 'device_no', label: '设备编号', type: 'readonly', readonlyOnEdit: true, placeholder: '保存后自动生成' },
    { key: 'model_name', label: '设备型号', type: 'select', optionSource: 'device_models', required: true, help: '型号决定可采集指标，主数据维护后立即生效' },
    { key: 'device_type', label: '设备类型', type: 'select', options: ['血压计', '血糖仪', '智能手环', '血氧仪', '体脂秤', '心电记录仪'].map(value => ({ value, label: value })), required: true },
    { key: 'user_id', label: '绑定用户', type: 'select', optionSource: 'users' },
    { key: 'region_id', label: '所属区域', type: 'region', optionSource: 'regions' },
    { key: 'status', label: '状态', type: 'select', options: toOptions(DEVICE_STATUS), defaultValue: 'unbound' },
    { key: 'firmware', label: '固件版本', type: 'text', placeholder: '如 v2.1.0' },
  ],
  createLabel: '新增设备',
  createPermission: 'devices.manage',
  editPermission: 'devices.manage',
  exportPermission: 'devices.export',
  exportScope: 'current-page',
  extraActions: [
    { key: 'models', label: '设备型号', icon: 'device', kind: 'link', to: '#/devices?panel=models', permission: 'devices.manage' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'devices.manage', tone: 'primary' },
    { key: 'bind', label: '绑定用户', permission: 'devices.bind', form: [
      { key: 'user_id', label: '绑定用户', type: 'select', optionSource: 'users', required: true },
      { key: 'family_id', label: '所属家庭', type: 'select', optionSource: 'families' },
    ], visibleWhen: { field: 'status', in: ['unbound', 'offline', 'online', 'fault'] } },
    { key: 'unbind', label: '解绑', permission: 'devices.bind', tone: 'danger', confirm: true, form: [{ key: 'reason', label: '解绑原因', type: 'textarea', required: true, full: true, placeholder: '解绑后设备停止上报，请填写原因' }], visibleWhen: { field: 'status', in: ['online', 'offline', 'fault'] } },
    { key: 'sync', label: '下发同步', permission: 'devices.command' },
    { key: 'firmware', label: '固件升级', permission: 'devices.command', confirm: true },
    { key: 'fault', label: '标记故障', permission: 'devices.manage', tone: 'danger', form: [{ key: 'fault_note', label: '故障说明', type: 'textarea', required: true, full: true }], visibleWhen: { field: 'status', in: ['online', 'offline'] } },
    { key: 'resolve', label: '恢复设备', permission: 'devices.manage', visibleWhen: { field: 'status', in: ['fault'] } },
  ],
  batchActions: [
    { key: 'batch-sync', label: '批量同步', permission: 'devices.command', kind: 'task' },
    { key: 'batch-export', label: '导出当前页', permission: 'devices.export', kind: 'export' },
  ],
  detailSections: [
    { title: '设备信息', fields: ['device_no', 'model_name', 'device_type', 'user_name', 'family_name', 'region_name', 'status'] },
    { title: '运行状态', fields: ['battery', 'firmware', 'last_report_at', 'bound_at', 'fault_note'] },
  ],
  notice: '解绑设备会停止健康数据上报；固件升级与同步命令以异步任务下发。',
  emptyHint: '当前筛选下没有设备。',
  numberPrefix: 'SB',
  seedKey: 'devices',
}
